import { useState, useRef, useEffect } from 'react'
import type { VocabularyTerm } from '../../stores/libraryStore'

interface CompetencyTagPickerProps {
  selectedTags: string[]
  vocabulary: VocabularyTerm[]
  onChange: (tags: string[]) => void
  label?: string
}

export default function CompetencyTagPicker({
  selectedTags,
  vocabulary,
  onChange,
  label = 'Capabilities',
}: CompetencyTagPickerProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filtered = vocabulary.filter(
    (t) =>
      t.term.toLowerCase().includes(query.toLowerCase()) &&
      !selectedTags.includes(t.term)
  )

  const addTag = (term: string) => {
    onChange([...selectedTags, term])
    setQuery('')
  }

  const removeTag = (term: string) => {
    onChange(selectedTags.filter((t) => t !== term))
  }

  const formatLabel = (term: string) =>
    term.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-xs font-medium text-stone-600 mb-1">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {selectedTags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-0.5 text-xs text-stone-700"
          >
            {formatLabel(tag)}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="text-stone-400 hover:text-stone-600 ml-0.5"
            >
              &times;
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setIsOpen(true)
        }}
        onFocus={() => setIsOpen(true)}
        placeholder="Search capabilities..."
        className="w-full rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-800 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none"
      />
      {isOpen && filtered.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded border border-stone-200 bg-white shadow-md">
          {filtered.slice(0, 20).map((term) => (
            <li key={term.term}>
              <button
                type="button"
                onClick={() => addTag(term.term)}
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-stone-50"
              >
                <span className="font-medium text-stone-700">{formatLabel(term.term)}</span>
                <span className="block text-xs text-stone-400">{term.definition}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
